import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Wrench, HardDrive, Wifi, ShieldCheck, Laptop, GraduationCap, Clock, BadgeEuro, Building2, Home, CheckCircle2, Loader2, ArrowRight } from "lucide-react";

const PHONE = "06 60 91 67 64";
const EMAIL = "contact@pcup.fr";
const ADDRESS = "5 Place Aimé et Marie Geoffroy, 91390 Morsang-sur-Orge";

const SERVICES = [
  { icon: Wrench, title: "Dépannage & réparation", text: "PC et Mac lents, pannes, écrans bleus, virus : diagnostic clair et réparation sur place ou en atelier." },
  { icon: HardDrive, title: "Récupération de données", text: "Disque dur, SSD, clé USB ou carte SD défaillants : nous récupérons vos fichiers, photos et documents." },
  { icon: Wifi, title: "Réseau & Wi-Fi", text: "Installation de box, répéteurs, câblage, imprimantes réseau. Un Wi-Fi stable dans toute la maison ou l'entreprise." },
  { icon: ShieldCheck, title: "Sécurité & sauvegardes", text: "Antivirus, mises à jour, sauvegardes automatiques et plan de reprise pour ne plus jamais perdre vos données." },
  { icon: Laptop, title: "Installation & configuration", text: "Nouveau poste, migration Windows / macOS, messagerie, Microsoft 365, transfert de vos données." },
  { icon: GraduationCap, title: "Accompagnement & formation", text: "Prise en main pas à pas, conseils d'achat et assistance à distance pour particuliers et équipes." },
];

const WHY = [
  { icon: Clock, title: "Intervention rapide", text: "Sous 24 à 48 h en Essonne et en Île-de-France, à domicile ou en entreprise." },
  { icon: BadgeEuro, title: "Devis gratuit, prix clairs", text: "Un diagnostic honnête et un tarif annoncé avant toute intervention. Pas de surprise." },
  { icon: Home, title: "Particuliers", text: "Un interlocuteur unique et pédagogue, qui parle votre langue — pas celle des machines." },
  { icon: Building2, title: "Professionnels & TPE", text: "Maintenance, contrats d'assistance et conseils pour que votre parc informatique ne freine jamais votre activité." },
];

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", city: "", need: "", client_type: "particulier", website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#8b1e3f] focus:ring-2 focus:ring-[#8b1e3f]/15 transition";

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/contact`, form);
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Impossible d'envoyer la demande, appelez-nous directement.");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="public-contact-success" className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
        <div className="font-heading text-xl font-bold text-slate-900">Demande bien reçue !</div>
        <p className="text-slate-600 mt-2">Florence vous rappelle très vite. En cas d'urgence : <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="font-semibold text-[#8b1e3f]">{PHONE}</a>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="public-contact-form" className="space-y-4">
      <div className="flex gap-2 p-1 rounded-xl bg-slate-100 w-fit">
        {[["particulier", "Particulier"], ["professionnel", "Professionnel"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`contact-type-${k}`} onClick={() => setForm({ ...form, client_type: k })} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${form.client_type === k ? "bg-white shadow text-[#8b1e3f]" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Votre nom *" className={field} />
        <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Votre email *" className={field} />
        <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="Téléphone" className={field} />
        <input data-testid="contact-city-input" value={form.city} onChange={set("city")} placeholder="Ville" className={field} />
        {form.client_type === "professionnel" && <input data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Entreprise" className={`${field} sm:col-span-2`} />}
      </div>
      <textarea data-testid="contact-need-input" required rows={4} value={form.need} onChange={set("need")} placeholder="Décrivez votre besoin : panne, matériel concerné, urgence… *" className={field} />
      <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {error && <div data-testid="contact-error" className="text-sm text-rose-600">{error}</div>}
      <button data-testid="contact-submit-button" type="submit" disabled={status === "loading"} className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#8b1e3f] hover:bg-[#731934] text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-60">
        {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Demander un rappel gratuit
      </button>
      <p className="text-xs text-slate-500">Vos informations servent uniquement à traiter votre demande. Jamais revendues.</p>
    </form>
  );
}

export default function PublicSite() {
  useEffect(() => {
    const prev = document.title;
    document.title = "PC UP — L'informatique à votre service | Dépannage informatique Essonne & Île-de-France";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f6f3] text-slate-800 font-body overflow-x-hidden" data-testid="public-site">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between gap-4">
          <a href="#top" className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="PC UP" className="w-11 h-11 object-contain" />
            <div>
              <div className="font-heading font-extrabold text-xl tracking-tight text-[#8b1e3f] leading-none">PC UP</div>
              <div className="text-[11px] italic text-[#1e3a8a] whitespace-nowrap">L'informatique à votre service</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#services" className="hover:text-[#8b1e3f]">Services</a>
            <a href="#pourquoi" className="hover:text-[#8b1e3f]">Pourquoi PC UP</a>
            <a href="#zone" className="hover:text-[#8b1e3f]">Zone d'intervention</a>
            <a href="#contact" className="hover:text-[#8b1e3f]">Contact</a>
          </nav>
          <a data-testid="public-call-button" href={`tel:${PHONE.replace(/\s/g, "")}`} className="px-4 py-2.5 rounded-xl bg-[#1e3a8a] hover:bg-[#172d6b] text-white text-sm font-semibold flex items-center gap-2 transition whitespace-nowrap shrink-0">
            <Phone className="w-4 h-4" /> <span className="hidden sm:inline">{PHONE}</span><span className="sm:hidden">Appeler</span>
          </a>
        </div>
      </header>

      <section id="top" className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#8b1e3f]/10 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#1e3a8a]/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1e3a8a] mb-6"><MapPin className="w-3.5 h-3.5" /> Morsang-sur-Orge · Essonne · Île-de-France</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Votre informatique <span className="text-[#8b1e3f]">réparée</span>, <span className="text-[#1e3a8a]">sécurisée</span>, expliquée.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              PC UP dépanne, installe et entretient les ordinateurs des particuliers et des petites entreprises, à domicile ou dans vos locaux. Un diagnostic honnête, un tarif annoncé à l'avance, et des explications claires.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a data-testid="hero-contact-button" href="#contact" className="px-7 py-3.5 rounded-xl bg-[#8b1e3f] hover:bg-[#731934] text-white font-semibold flex items-center justify-center gap-2 transition">Demander un rappel gratuit <ArrowRight className="w-4 h-4" /></a>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="px-7 py-3.5 rounded-xl bg-white border border-slate-200 hover:border-[#1e3a8a] text-slate-800 font-semibold flex items-center justify-center gap-2 transition"><Phone className="w-4 h-4 text-[#1e3a8a]" /> {PHONE}</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {["Devis gratuit", "Intervention 24-48 h", "Particuliers & pros", "Déplacement en Île-de-France"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl bg-white border border-slate-200 shadow-xl p-8 lg:p-10">
              <img src="/logo.png" alt="PC UP — L'informatique à votre service" className="w-full max-w-xs mx-auto object-contain" />
              <div className="mt-8 grid grid-cols-3 gap-3 text-center">
                {[["24-48 h", "délai moyen"], ["91 & IDF", "zone couverte"], ["100 %", "devis gratuit"]].map(([v, l]) => (
                  <div key={l} className="rounded-2xl bg-[#f7f6f3] p-4"><div className="font-heading font-extrabold text-xl text-[#8b1e3f]">{v}</div><div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{l}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1e3a8a]">Nos services</div>
          <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mt-2">Tout ce dont votre informatique a besoin, par une seule personne de confiance.</h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <div key={s.title} className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-[#8b1e3f]/40 hover:shadow-lg transition">
              <div className="w-11 h-11 rounded-xl bg-[#8b1e3f]/10 text-[#8b1e3f] flex items-center justify-center group-hover:bg-[#8b1e3f] group-hover:text-white transition"><s.icon className="w-5 h-5" /></div>
              <div className="font-heading font-bold text-slate-900 mt-4">{s.title}</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pourquoi" className="bg-[#1e3a8a] text-white">
        <div className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">Pourquoi PC UP</div>
            <h2 className="font-heading text-base md:text-lg font-bold mt-2">Une informatique qui marche, et que vous comprenez.</h2>
            <p className="text-sky-100/80 mt-4 leading-relaxed">Fondée par Florence Lebouc, PC UP est une entreprise de proximité : vous parlez toujours à la même personne, du diagnostic à la facture.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-2xl bg-white/10 border border-white/15 p-6 backdrop-blur">
                <w.icon className="w-6 h-6 text-sky-200" />
                <div className="font-heading font-bold mt-3">{w.title}</div>
                <p className="text-sm text-sky-100/80 mt-1.5 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="zone" className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1e3a8a]">Zone d'intervention</div>
          <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mt-2">Basés à Morsang-sur-Orge, nous nous déplaçons dans toute l'Île-de-France.</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">Essonne (91) en priorité — Savigny-sur-Orge, Sainte-Geneviève-des-Bois, Évry-Courcouronnes, Brétigny, Corbeil-Essonnes… — et sur demande Paris, Hauts-de-Seine, Val-de-Marne, Seine-et-Marne, Yvelines, Seine-Saint-Denis et Val-d'Oise. Assistance à distance possible partout.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["91 Essonne", "75 Paris", "92", "93", "94", "77", "78", "95"].map((d) => <span key={d} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700">{d}</span>)}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
          <div className="font-heading font-bold text-slate-900 text-lg">Coordonnées</div>
          <div className="mt-5 space-y-4 text-slate-700">
            <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#8b1e3f] mt-0.5" /><div><div className="text-xs uppercase tracking-wider text-slate-500">Téléphone</div><a href={`tel:${PHONE.replace(/\s/g, "")}`} className="font-semibold hover:text-[#8b1e3f]">{PHONE}</a></div></div>
            <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#8b1e3f] mt-0.5" /><div><div className="text-xs uppercase tracking-wider text-slate-500">Email</div><a href={`mailto:${EMAIL}`} className="font-semibold hover:text-[#8b1e3f]">{EMAIL}</a></div></div>
            <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#8b1e3f] mt-0.5" /><div><div className="text-xs uppercase tracking-wider text-slate-500">Adresse</div><div className="font-semibold">{ADDRESS}</div></div></div>
          </div>
        </div>
      </section>

      <section id="contact" className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-12">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1e3a8a]">Contact</div>
            <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mt-2">Décrivez votre problème, on vous rappelle gratuitement.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">Réponse sous 24 h ouvrées. Pour une urgence, appelez directement le <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="font-semibold text-[#8b1e3f]">{PHONE}</a>.</p>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="PC UP" className="w-9 h-9 object-contain bg-white rounded-lg p-0.5" />
            <div><div className="font-heading font-bold text-white">PC UP</div><div className="text-xs italic">L'informatique à votre service</div></div>
          </div>
          <div className="text-xs leading-relaxed">Florence Lebouc · {ADDRESS}<br />{PHONE} · {EMAIL}</div>
          <Link to="/login" data-testid="public-login-link" className="text-xs text-slate-500 hover:text-white">Espace pro</Link>
        </div>
      </footer>
    </div>
  );
}
